import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import MultiChoice from '@components/choice/MultiChoice'
import TextInput from '@components/textInput/TextInput'
import { Animation } from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'

interface Values {
  manifestations: string[]
}

const Manif = ({ form, onForm }: AdminUserFormProps) => {
  const init: Values = React.useMemo(() => ({ manifestations: form.manifestations }), [form.manifestations])

  const i18n = useI18n()

  const manifestations = useReducer(AdminStore.store, (s) => s.manifestations)

  const [search, setSearch] = React.useState('')

  const manifs = React.useMemo(
    () => manifestations.filter((m) => sanitize(m.libelle).includes(sanitize(search))),
    [manifestations, search]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const allOptions = React.useMemo(
    () => manifestations.map((m) => ({ label: m.libelle, value: m.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [manifestations]
  )

  const options = React.useMemo(
    () => manifs.map((m) => ({ label: m.libelle, value: m.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [manifs]
  )

  const opts = allOptions.filter((o) => values.manifestations.includes(o.value))

  return (
    <MainContainer>
      <Animation>
        <SearchContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('screens.incident.POSITION.search')}
            left={{ name: 'search' }}
          />
        </SearchContainer>
      </Animation>

      {options.length > 0 ? (
        <MultiChoice
          data={options}
          onItemSelected={(next) =>
            setFieldValue(
              'manifestations',
              next.map((o) => o.value)
            )
          }
          value={opts}
          inline={false}
        />
      ) : (
        <MissingInfo>{i18n.t('screens.admin.users.form.manif.noManif')}</MissingInfo>
      )}
    </MainContainer>
  )
}

export default Manif

const MainContainer = styled(View)`
  margin: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 48)}px;
  margin-top: 25px;
  flex: 1;
`

const SearchContainer = styled(View)`
  margin-bottom: 25px;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
